import { Injectable } from '@angular/core';
import { AuthService } from '../../auth-pages/services/auth.service';
import {
  ChatflowPermission,
  ROLE_PERMISSION_MAP
} from '../models/chatflow-permission.model';

/**
 * Servicio frontend de permisos de chatflow.
 * Mapea roles a permisos como fallback hasta que exista un endpoint específico de permisos.
 * Preparado para integrarse con GET /api/users/me/chatflow-permissions cuando el backend lo exponga.
 */
@Injectable({ providedIn: 'root' })
export class ChatflowPermissionService {
  constructor(private auth: AuthService) {}

  private get roleName(): string {
    return this.auth.getCurrentUser()?.roleName ?? '';
  }

  private get permissions(): ChatflowPermission[] {
    return ROLE_PERMISSION_MAP[this.roleName] ?? ROLE_PERMISSION_MAP['Agent'] ?? [];
  }

  hasPermission(permission: ChatflowPermission): boolean {
    return this.permissions.includes(permission);
  }

  canView(): boolean { return this.hasPermission('CHATFLOW_VIEW'); }
  canCreate(): boolean { return this.hasPermission('CHATFLOW_CREATE'); }
  canEdit(): boolean { return this.hasPermission('CHATFLOW_EDIT'); }
  canPublish(): boolean { return this.hasPermission('CHATFLOW_PUBLISH'); }
  canArchive(): boolean { return this.hasPermission('CHATFLOW_ARCHIVE'); }
  canManageWidget(): boolean { return this.hasPermission('CHATFLOW_MANAGE_WIDGET'); }
  canManageSchedule(): boolean { return this.hasPermission('CHATFLOW_MANAGE_SCHEDULE'); }
  canManageRouting(): boolean { return this.hasPermission('CHATFLOW_MANAGE_ROUTING'); }
  canManageLocalization(): boolean { return this.hasPermission('CHATFLOW_MANAGE_LOCALIZATION'); }
  canManageDisplayRules(): boolean { return this.hasPermission('CHATFLOW_MANAGE_DISPLAY_RULES'); }
  canManageTicketAutomation(): boolean { return this.hasPermission('CHATFLOW_MANAGE_TICKET_AUTOMATION'); }
  canManageCsat(): boolean { return this.hasPermission('CHATFLOW_MANAGE_CSAT'); }
  canViewReports(): boolean { return this.hasPermission('CHATFLOW_VIEW_REPORTS'); }
  canViewTeamReports(): boolean { return this.hasPermission('CHATFLOW_VIEW_TEAM_REPORTS'); }
  canManagePrivacy(): boolean { return this.hasPermission('CHATFLOW_MANAGE_PRIVACY'); }
  canDeleteTranscripts(): boolean { return this.hasPermission('CHATFLOW_DELETE_TRANSCRIPTS'); }
  canExportTranscripts(): boolean { return this.hasPermission('CHATFLOW_EXPORT_TRANSCRIPTS'); }
  canViewAllConversations(): boolean { return this.hasPermission('CHATFLOW_VIEW_ALL_CONVERSATIONS'); }
  canTransferConversations(): boolean { return this.hasPermission('CHATFLOW_TRANSFER_CONVERSATIONS'); }

  /** Devuelve un mensaje amigable si no tiene el permiso */
  getPermissionDeniedMessage(permission: ChatflowPermission): string {
    const messages: Partial<Record<ChatflowPermission, string>> = {
      CHATFLOW_VIEW_REPORTS: 'No tienes permiso para ver reportes de chatflow. Solicita acceso a un administrador.',
      CHATFLOW_EXPORT_TRANSCRIPTS: 'No tienes permiso para exportar transcripciones.',
      CHATFLOW_DELETE_TRANSCRIPTS: 'No tienes permiso para eliminar transcripciones.',
      CHATFLOW_MANAGE_PRIVACY: 'No tienes permiso para gestionar la configuración de privacidad.',
      CHATFLOW_MANAGE_TICKET_AUTOMATION: 'No tienes permiso para configurar la automatización de tickets.',
      CHATFLOW_MANAGE_CSAT: 'No tienes permiso para configurar encuestas CSAT.',
      CHATFLOW_PUBLISH: 'No tienes permiso para publicar chatflows.',
    };
    return messages[permission] ?? 'No tienes permiso para realizar esta acción.';
  }
}
